/* eslint-disable no-bitwise */
import React, { memo, useEffect, useImperativeHandle, useRef } from "react";

import "./zone-selection-box.css";
import classNames from "classnames";
import { useFeatureFlagToggle } from "../../hooks/useFeatureFlags";
import { usePreviewBox } from "../../hooks/usePreviewBox";
import { Transform } from "../../transform";
import { useAppContext } from "../../hooks/useAppContext";
import { Icon } from "../icon";
import { useZoneRotate, zoneRotateState } from "../slideshow-preview/hooks/use-zone-rotate";
import { ZoneSelectionBoxHandlers } from "./zone-selection-box-handlers";

function updateZoneSelectionBox(
	selectedZone: eXpress.core.IZone | undefined,
	selectionBoxEl: HTMLElement,
	dipToScreenTransform: Transform,
	browserInfo: eXpress.platforms.IBrowserInfo,
) {
	const canSelectZone =
		selectedZone &&
		(eXpress.core.isAnyTextZone(selectedZone) ||
			eXpress.core.isPhotoZone(selectedZone) ||
			eXpress.core.isSymbolZone(selectedZone));

	selectionBoxEl.style.display = canSelectZone ? "block" : "none";

	if (!canSelectZone || !selectedZone) return;

	const rect = dipToScreenTransform.transformRect(selectedZone);

	// On iOS there was sometimes no left outline visible on the selection box.
	// This was caused when using a decimal number as left position in combination with outline styling.
	// To solve this, we do the position with CSS translate, this would also behave more smoothly during animations
	selectionBoxEl.style.left = "0";
	selectionBoxEl.style.top = "0";
	selectionBoxEl.style.width = `${eXpress.math.quant(
		rect.width,
		browserInfo.isIos() ? 1 : 10,
	)}px`;
	selectionBoxEl.style.height = `${eXpress.math.quant(
		rect.height,
		browserInfo.isIos() ? 1 : 10,
	)}px`;
	selectionBoxEl.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0) rotate(${selectedZone.angle}deg)`;
}

const ROTATE_HANDLE_POSITION_CANDIDATES = [
	"bottom",
	"top",
	"left",
	"right",
	"top-left",
	"top-right",
	"bottom-left",
	"bottom-right",
] as const;

type RotateHandlePosition = (typeof ROTATE_HANDLE_POSITION_CANDIDATES)[number];

function updateRotateHandlePosition(
	rotateHandleEl: HTMLElement | null,
	rotateHandleIconEl: HTMLElement | null,
	previewRect: eXpress.math.IRect,
) {
	if (!rotateHandleEl || !rotateHandleIconEl) return;

	const previewRect2 = eXpress.math.rect(previewRect);

	if (previewRect2.area() <= 0) {
		rotateHandleEl.dataset["rotatePosition"] = "bottom";
		return;
	}

	const currentPosition =
		(rotateHandleEl.dataset["rotatePosition"] as RotateHandlePosition | undefined) ?? "bottom";

	const handleArea = rotateHandleIconEl.offsetWidth * rotateHandleIconEl.offsetHeight;

	const margin = 4;
	const previewRectWithMargin = previewRect2.shrink(margin);

	const isFit = (visibleArea: number) => handleArea > 0 && visibleArea >= handleArea;

	for (const position of ROTATE_HANDLE_POSITION_CANDIDATES) {
		rotateHandleEl.dataset["rotatePosition"] = position;
		const domRect = rotateHandleIconEl.getBoundingClientRect();
		const rotateRect = eXpress.math.rect(
			domRect.left,
			domRect.top,
			domRect.width,
			domRect.height,
		);
		const visibleArea = previewRectWithMargin.intersect(rotateRect).area();
		if (isFit(visibleArea)) {
			return;
		}
	}

	rotateHandleEl.dataset["rotatePosition"] = currentPosition;
}

function showHandlers(
	selectionBoxEl: HTMLElement,
	handlersToShow: number,
	selectedZone: eXpress.core.IZone | undefined,
) {
	[
		ZoneSelectionBoxHandlers.Top,
		ZoneSelectionBoxHandlers.Left,
		ZoneSelectionBoxHandlers.Right,
		ZoneSelectionBoxHandlers.Bottom,
		ZoneSelectionBoxHandlers.TopLeft,
		ZoneSelectionBoxHandlers.TopRight,
		ZoneSelectionBoxHandlers.BottomLeft,
		ZoneSelectionBoxHandlers.BottomRight,
	].forEach((handle, index) => {
		const el = selectionBoxEl.children[index] as HTMLElement;
		if (el) {
			if ((handlersToShow & handle) > 0) {
				el.style.opacity = "1";
				el.style.pointerEvents = "auto";
			} else {
				el.style.opacity = "0";
				el.style.pointerEvents = "none";
			}

			el.style.cursor = getHandleCursor(handle, selectedZone);
		}
	});
}

/** Depending on the handle an zone rotation, we want to show the correct cursor */
function getHandleCursor(
	handle: ZoneSelectionBoxHandlers,
	selectedZone: eXpress.core.IZone | undefined,
) {
	/**
	 * Quadrants:
	 * >=0° -> <45° = 0
	 * >45°  -> <135° = 1
	 * >135° -> <225° = 2
	 * >225° -> <315° = 3
	 * >315° -> <360° = 0
	 */
	const quadrant = Math.floor((((selectedZone?.angle ?? 0) + 45) % 360) / 90);

	switch (handle) {
		case ZoneSelectionBoxHandlers.TopLeft:
			return quadrant % 2 ? "nesw-resize" : "nwse-resize";
		case ZoneSelectionBoxHandlers.TopRight:
			return quadrant % 2 ? "nwse-resize" : "nesw-resize";
		case ZoneSelectionBoxHandlers.BottomLeft:
			return quadrant % 2 ? "nwse-resize" : "nesw-resize";
		case ZoneSelectionBoxHandlers.BottomRight:
			return quadrant % 2 ? "nesw-resize" : "nwse-resize";
		case ZoneSelectionBoxHandlers.Top:
		case ZoneSelectionBoxHandlers.Bottom:
			return quadrant % 2 ? "ew-resize" : "ns-resize";
		case ZoneSelectionBoxHandlers.Left:
		case ZoneSelectionBoxHandlers.Right:
			return quadrant % 2 ? "ns-resize" : "ew-resize";
		case ZoneSelectionBoxHandlers.None:
		case ZoneSelectionBoxHandlers.All:
		case ZoneSelectionBoxHandlers.Corners:
		case ZoneSelectionBoxHandlers.Sides:
		default:
			return "auto";
	}
}

function showDefaultHandlers(
	selectedZone: eXpress.core.IZone | undefined,
	allowedHandles: ZoneSelectionBoxHandlers,
	selectionBoxEl: HTMLElement,
	dipToScreenTransform: Transform | undefined,
) {
	// Keep orientation into account

	// If only one handle, show always
	// Could als be tested with this function: https://www.w3resource.com/javascript-exercises/javascript-math-exercise-13.php
	// const isPowerOf2 = (n: number) => !!n && (n & (n - 1)) === 0;
	if (
		allowedHandles === ZoneSelectionBoxHandlers.TopLeft ||
		allowedHandles === ZoneSelectionBoxHandlers.Top ||
		allowedHandles === ZoneSelectionBoxHandlers.TopRight ||
		allowedHandles === ZoneSelectionBoxHandlers.Left ||
		allowedHandles === ZoneSelectionBoxHandlers.Right ||
		allowedHandles === ZoneSelectionBoxHandlers.BottomLeft ||
		allowedHandles === ZoneSelectionBoxHandlers.Bottom ||
		allowedHandles === ZoneSelectionBoxHandlers.BottomRight
	) {
		showHandlers(selectionBoxEl, allowedHandles, selectedZone); // always show
		return;
	}

	// Hide handlers because of small sizes
	if (!selectedZone || !dipToScreenTransform) return;
	if (dipToScreenTransform) {
		const screenRect = dipToScreenTransform.transformRect(selectedZone);

		const xs = 25;
		const s = 45;

		const isXsWidth = screenRect.width < xs;
		const isSWidth = !isXsWidth && screenRect.width < s;
		const isMWidth = !isXsWidth && !isSWidth;
		const isXsHeight = screenRect.height < xs;
		const isSHeight = !isXsHeight && screenRect.height < s;
		const isMHeight = !isXsHeight && !isSHeight;

		let handles = ZoneSelectionBoxHandlers.TopRight;

		// don't allow top/bottom/left/right handles for zones with an overlay/mask
		if (
			isMWidth &&
			isMHeight &&
			eXpress.core.isPhotoZone(selectedZone) &&
			!!selectedZone.effect
		) {
			handles = ZoneSelectionBoxHandlers.Corners;
		} else {
			if (isMWidth && isSHeight) {
				handles =
					ZoneSelectionBoxHandlers.Corners |
					ZoneSelectionBoxHandlers.Top |
					ZoneSelectionBoxHandlers.Bottom;
			}
			if (isSWidth && isMHeight) {
				handles =
					ZoneSelectionBoxHandlers.Corners |
					ZoneSelectionBoxHandlers.Left |
					ZoneSelectionBoxHandlers.Right;
			}

			// Only one of these will match
			// See schema for more info: https://smartphoto.atlassian.net/browse/CRE-2988?focusedCommentId=518464
			if (isXsWidth && isXsHeight) handles = ZoneSelectionBoxHandlers.TopRight;
			if (isXsWidth && isSHeight) handles = ZoneSelectionBoxHandlers.TopRight;
			if (isXsWidth && isMHeight) handles = ZoneSelectionBoxHandlers.TopRight;

			if (isSWidth && isXsHeight) handles = ZoneSelectionBoxHandlers.TopRight;
			if (isSWidth && isSHeight) handles = ZoneSelectionBoxHandlers.Corners;

			if (isMWidth && isXsHeight) handles = ZoneSelectionBoxHandlers.TopRight;
			if (isMWidth && isSHeight) {
				handles =
					ZoneSelectionBoxHandlers.Corners |
					ZoneSelectionBoxHandlers.Top |
					ZoneSelectionBoxHandlers.Bottom;
			}
			if (isMWidth && isMHeight) handles = ZoneSelectionBoxHandlers.All;
		}

		showHandlers(selectionBoxEl, handles, selectedZone);
	} else {
		// Show all allowed
		showHandlers(selectionBoxEl, ZoneSelectionBoxHandlers.All, selectedZone);
	}
}

export interface IZoneSelectionBoxRef {
	el: HTMLDivElement;

	/** Update the position/size of the selectionBox and handles */
	updateZoneSelectionBox(
		selectedZone: eXpress.core.IZone | undefined,
		dipToScreenTransform: Transform,
	): void;

	/** Automatically show the handles based on available size */
	showDefaultHandlers(
		selectedZone: eXpress.core.IZone | undefined,
		dipToScreenTransform: Transform,
		allowedHandles: ZoneSelectionBoxHandlers,
	): void;
}

interface IZoneSelectionBoxProps {
	/** The handles that the user can drag on the zone selection box */
	handles?: ZoneSelectionBoxHandlers;
	/** Whether to display the rotate handle on the zone selection box. Set to true to allow the user to rotate the selected zone. */
	showRotateHandle?: boolean;
}

function CornerHandle({ className, isAllowed }: { className: string; isAllowed: boolean }) {
	const className2 = classNames("zone-selection-box--corner", className, {
		"zone-selection-box--hidden": !isAllowed, // Hide with class instead of remove, so we can use fade-out animation, also faster because no repaint needed
	});

	return (
		<div className={className2}>
			<div />
		</div>
	);
}

function SideHandle({ className, isAllowed }: { className: string; isAllowed: boolean }) {
	const className2 = classNames("zone-selection-box--side", className, {
		"zone-selection-box--hidden": !isAllowed, // Hide with class instead of remove, so we can use fade-out animation, also faster because no repaint needed
	});

	return (
		<div className={className2}>
			<div />
		</div>
	);
}

function RotateHandle({
	className,
	isAllowed,
	rotateHandleRef,
	rotateHandleIconRef,
}: {
	className: string;
	isAllowed: boolean;
	rotateHandleRef: React.Ref<HTMLDivElement>;
	rotateHandleIconRef: React.Ref<HTMLDivElement>;
}) {
	const className2 = classNames("zone-selection-box--rotate", className, {
		"zone-selection-box--hidden": !isAllowed, // Hide with class instead of remove, so we can use fade-out animation, also faster because no repaint needed
	});

	return (
		<div
			className={className2}
			data-rotate-handle
			data-rotate-position="bottom"
			ref={rotateHandleRef}
		>
			<div ref={rotateHandleIconRef}>
				<Icon iconName="rotate" />
			</div>
		</div>
	);
}

export const ZoneSelectionBox = memo(
	({
		handles: allowedHandles = ZoneSelectionBoxHandlers.None,
		showRotateHandle,
		ref,
	}: IZoneSelectionBoxProps & { ref?: React.Ref<IZoneSelectionBoxRef> }) => {
		/** Ref to DOM element */
		const divRef = useRef<HTMLDivElement>(null);
		const isDebugSelectionBox = useFeatureFlagToggle("debugSelectionBox");
		const { platform } = useAppContext();
		const { previewBox } = usePreviewBox();
		const rotateHandleRef = useRef<HTMLDivElement>(null);
		const rotateHandleIconRef = useRef<HTMLDivElement>(null);

		const [isZoneRotating] = useZoneRotate();

		useEffect(() => {
			if (!showRotateHandle || isZoneRotating) return undefined;
			const rafId = requestAnimationFrame(() => {
				updateRotateHandlePosition(
					rotateHandleRef.current,
					rotateHandleIconRef.current,
					previewBox,
				);
			});
			return () => cancelAnimationFrame(rafId);
		}, [previewBox, showRotateHandle, isZoneRotating]);

		/** Expose ZoneSelectionBox methods via ref */
		useImperativeHandle(
			ref,
			() => {
				const el = divRef.current;
				if (!el) return null as unknown as IZoneSelectionBoxRef;

				return {
					el,
					updateZoneSelectionBox: (
						selectedZone: eXpress.core.IZone | undefined,
						dipToScreenTransform: Transform,
					) => {
						updateZoneSelectionBox(
							selectedZone,
							el,
							dipToScreenTransform,
							platform.browserInfo,
						);

						if (
							showRotateHandle &&
							selectedZone?.canSelect() &&
							!zoneRotateState.isZoneRotating
						) {
							updateRotateHandlePosition(
								rotateHandleRef.current,
								rotateHandleIconRef.current,
								previewBox,
							);
						}
					},

					showDefaultHandlers: (
						selectedZone: eXpress.core.IZone | undefined,
						dipToScreenTransform: Transform,
						// eslint-disable-next-line @typescript-eslint/no-shadow
						allowedHandles: ZoneSelectionBoxHandlers,
					) => {
						showDefaultHandlers(selectedZone, allowedHandles, el, dipToScreenTransform);
					},
				};
			},
			[platform.browserInfo, previewBox, showRotateHandle],
		);

		return (
			<div
				className={`zone-selection-box ${
					isDebugSelectionBox ? "zone-selection-box--debug" : ""
				}`}
				ref={divRef}
			>
				<SideHandle
					className="zone-selection-box--top"
					isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.Top) !== 0}
				/>
				<SideHandle
					className="zone-selection-box--left"
					isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.Left) !== 0}
				/>
				<SideHandle
					className="zone-selection-box--right"
					isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.Right) !== 0}
				/>
				<SideHandle
					className="zone-selection-box--bottom"
					isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.Bottom) !== 0}
				/>

				<CornerHandle
					className="zone-selection-box--topleft"
					isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.TopLeft) !== 0}
				/>
				<CornerHandle
					className="zone-selection-box--topright"
					isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.TopRight) !== 0}
				/>
				<CornerHandle
					className="zone-selection-box--bottomleft"
					isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.BottomLeft) !== 0}
				/>
				<CornerHandle
					className="zone-selection-box--bottomright"
					isAllowed={(allowedHandles & ZoneSelectionBoxHandlers.BottomRight) !== 0}
				/>
				<RotateHandle
					className="zone-selection-box--rotate"
					isAllowed={!!showRotateHandle}
					rotateHandleRef={rotateHandleRef}
					rotateHandleIconRef={rotateHandleIconRef}
				/>
			</div>
		);
	},
);
ZoneSelectionBox.displayName = "ZoneSelectionBox";
